import { useEffect, useMemo, useState } from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { addDoc, collection, doc, onSnapshot, query, serverTimestamp, updateDoc, where, } from "firebase/firestore";
import { LayoutDashboard, Shirt, PackageCheck, Wallet, LogOut, Menu, X, Bell, Plus, Pencil, Eye, EyeOff, Upload, Truck, Clock3, IndianRupee, Boxes, CircleAlert, CheckCircle2, ImagePlus, RefreshCw, } from "lucide-react";
import { auth, db } from "../../firebase";
const nav = [
    { id: "home", label: "Home", icon: LayoutDashboard },
    { id: "products", label: "Upload & Edit Sarees", icon: Shirt },
    { id: "orders", label: "Orders to Pack", icon: PackageCheck },
    { id: "wallet", label: "Wallet & Withdraw", icon: Wallet },
    { id: "training", label: "Enquire", icon: CircleAlert },
];
const money = (n) => new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
}).format(Number(n) || 0);
const dateText = (v) => {
    if (!v)
        return "—";
    const d = v?.toDate ? v.toDate() : new Date(v);
    return Number.isNaN(d.getTime())
        ? "—"
        : d.toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
};
const pretty = (s) => String(s || "pending").replaceAll("_", " ");
const initialForm = {
    name: "",
    description: "",
    category: "Silk",
    fixedPrice: "",
    stock: "",
};
function Pill({ status }) {
    const s = String(status || "pending").toLowerCase();
    const cls = [
        "active",
        "live",
        "delivered",
        "paid",
        "approved",
        "completed",
    ].includes(s)
        ? "bg-emerald-50 text-emerald-700"
        : [
            "ready_to_ship",
            "shipped",
            "in_transit",
            "processing",
        ].includes(s)
            ? "bg-sky-50 text-sky-700"
            : ["hidden", "inactive", "cancelled", "rejected"].includes(s)
                ? "bg-red-50 text-red-700"
                : "bg-amber-50 text-amber-800";
    return (<span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold capitalize ${cls}`}>
      {pretty(status)}
    </span>);
}
function Stat({ icon: Icon, label, value, note, tone = "rose" }) {
    const tones = {
        rose: "bg-rose-50 text-rose-800",
        gold: "bg-amber-50 text-amber-800",
        green: "bg-emerald-50 text-emerald-700",
        blue: "bg-sky-50 text-sky-700",
    };
    return (<div className="rounded-2xl border border-stone-200 bg-white p-4 sm:p-5 shadow-sm">
      <div className="flex justify-between gap-3">
        <div>
          <p className="text-sm text-stone-500">{label}</p>
          <p className="mt-2 text-2xl font-bold text-stone-900">{value}</p>
          <p className="mt-2 text-xs text-stone-500">{note}</p>
        </div>
        <div className={`rounded-xl p-3 ${tones[tone]}`}>
          <Icon size={21}/>
        </div>
      </div>
    </div>);
}
function Empty({ title, text }) {
    return (<div className="rounded-2xl border border-dashed border-stone-300 bg-white px-5 py-12 text-center">
      <Boxes className="mx-auto text-stone-400" size={30}/>
      <h3 className="mt-3 font-semibold text-stone-800">{title}</h3>
      <p className="mx-auto mt-1 max-w-md text-sm text-stone-500">
        {text}
      </p>
    </div>);
}
// Read and compress a product photo in the browser, similar to the
// payment-screenshot flow. Firestore documents are limited to 1 MiB, so
// keep the resulting data URL small before saving it in the product document.
function compressImageToDataUrl(file, maxDimension = 900, quality = 0.68) {
    return new Promise((resolve, reject) => {
        if (!file || !file.type?.startsWith("image/")) {
            reject(new Error("Please select a valid image file."));
            return;
        }
        const reader = new FileReader();
        reader.onerror = () => reject(new Error("Could not read the selected image."));
        reader.onload = () => {
            const image = new Image();
            image.onerror = () => reject(new Error("This image could not be opened."));
            image.onload = () => {
                const scale = Math.min(1, maxDimension / Math.max(image.width, image.height));
                const canvas = document.createElement("canvas");
                canvas.width = Math.max(1, Math.round(image.width * scale));
                canvas.height = Math.max(1, Math.round(image.height * scale));
                const context = canvas.getContext("2d");
                if (!context) {
                    reject(new Error("Image compression is not supported by this browser."));
                    return;
                }
                context.drawImage(image, 0, 0, canvas.width, canvas.height);
                const dataUrl = canvas.toDataURL("image/jpeg", quality);
                // Keep a safety margin below Firestore's 1 MiB document limit.
                if (dataUrl.length > 650000) {
                    reject(new Error("Photo is still too large after compression. Choose a smaller photo."));
                    return;
                }
                resolve(dataUrl);
            };
            image.src = String(reader.result);
        };
        reader.readAsDataURL(file);
    });
}
export default function ShopOwnerDashboard() {
    const [user, setUser] = useState(null);
    const [profile, setProfile] = useState(null);
    const [products, setProducts] = useState([]);
    const [orders, setOrders] = useState([]);
    const [withdrawals, setWithdrawals] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [notificationsOpen, setNotificationsOpen] = useState(false);
    const [readNotificationIds, setReadNotificationIds] = useState(() => {
        try {
            return JSON.parse(window.localStorage.getItem("sareeStudioShopOwnerReadNotifications") || "[]");
        }
        catch {
            return [];
        }
    });
    const [tab, setTab] = useState("home");
    const [mobileOpen, setMobileOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");
    const [search, setSearch] = useState("");
    const [orderFilter, setOrderFilter] = useState("all");
    const [modal, setModal] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(initialForm);
    const [imageFile, setImageFile] = useState(null);
    const [withdrawAmount, setWithdrawAmount] = useState("");
    const [upi, setUpi] = useState("");
    const [question, setQuestion] = useState("");
    const [enquirySubject, setEnquirySubject] = useState("Order / delivery issue");
    const [enquiryMessage, setEnquiryMessage] = useState("");
    const [enquirySaving, setEnquirySaving] = useState(false);
    const [enquiryNotice, setEnquiryNotice] = useState("");
    const [enquiries, setEnquiries] = useState([]);
    useEffect(() => {
        let profileUnsub;
        let productsUnsub;
        let ordersUnsub;
        let withdrawalsUnsub;
        let transactionsUnsub;
        let enquiriesUnsub;
        const authUnsub = onAuthStateChanged(auth, (current) => {
            [
                profileUnsub,
                productsUnsub,
                ordersUnsub,
                withdrawalsUnsub,
                transactionsUnsub,
                enquiriesUnsub,
            ]
                .filter(Boolean)
                .forEach((fn) => fn());
            setUser(current || null);
            setProfile(null);
            setProducts([]);
            setOrders([]);
            setWithdrawals([]);
            setTransactions([]);
            setEnquiries([]);
            if (!current) {
                setLoading(false);
                return;
            }
            const uid = current.uid;
            profileUnsub = onSnapshot(doc(db, "users", uid), (snap) => {
                setProfile(snap.exists() ? snap.data() : null);
                setLoading(false);
            }, (e) => {
                setError(`Profile load failed: ${e.message}`);
                setLoading(false);
            });
            productsUnsub = onSnapshot(query(collection(db, "products"), where("shopOwnerId", "==", uid)), (snap) => setProducts(snap.docs.map((d) => ({ id: d.id, ...d.data() }))), (e) => setError(`Products load failed: ${e.message}`));
            ordersUnsub = onSnapshot(query(collection(db, "orders"), where("shopOwnerId", "==", uid)), (snap) => setOrders(snap.docs.map((d) => ({ id: d.id, ...d.data() }))), (e) => setError(`Orders load failed: ${e.message}`));
            withdrawalsUnsub = onSnapshot(query(collection(db, "withdrawals"), where("shopOwnerId", "==", uid)), (snap) => setWithdrawals(snap.docs.map((d) => ({ id: d.id, ...d.data() }))), (e) => console.warn("Withdrawals unavailable", e.message));
            transactionsUnsub = onSnapshot(query(collection(db, "transactions"), where("userId", "==", uid)), (snap) => setTransactions(snap.docs.map((d) => ({ id: d.id, ...d.data() }))), (e) => console.warn("Transactions unavailable", e.message));
            enquiriesUnsub = onSnapshot(query(collection(db, "enquiries"), where("userId", "==", uid)), (snap) => setEnquiries(snap.docs.map((d) => ({ id: d.id, ...d.data() }))), (e) => console.warn("Enquiries unavailable", e.message));
        });
        return () => {
            authUnsub();
            [
                profileUnsub,
                productsUnsub,
                ordersUnsub,
                withdrawalsUnsub,
                transactionsUnsub,
                enquiriesUnsub,
            ]
                .filter(Boolean)
                .forEach((fn) => fn());
        };
    }, []);
    const live = products.filter((p) => !["hidden", "inactive", "disabled", "archived"].includes(String(p.status || p.visibility || "active").toLowerCase()));
    const lowStock = live.filter((p) => Number(p.stock ?? p.stockQuantity ?? 0) <= 3);
    const toPack = orders.filter((o) => ["pending", "to_pack", "processing", "paid"].includes(String(o.status || o.orderStatus || "pending").toLowerCase()));
    const ready = orders.filter((o) => ["ready_to_ship", "ready to ship"].includes(String(o.status || o.orderStatus || "").toLowerCase()));
    const transit = orders.filter((o) => ["in_transit", "shipped", "in transit"].includes(String(o.status || o.orderStatus || "").toLowerCase()));
    // Older shop-owner profiles may not have wallet fields yet. Fall back to
    // explicitly recorded shop-owner earnings on delivered orders for display.
    const deliveredOrders = orders.filter((o) => String(o.status || o.orderStatus || "").toLowerCase() === "delivered");
    const deliveredOwnerEarnings = deliveredOrders.reduce((sum, o) => sum + Number(o.shopOwnerEarning ?? o.shopOwnerProfit ?? o.ownerEarning ?? o.shopOwnerAmount ?? o.shopOwnerSubtotal ?? o.baseAmount ?? 0), 0);
    const savedBalance = profile?.walletBalance ?? profile?.wallet?.balance ?? profile?.availableBalance;
    const savedEarned = profile?.totalEarned ?? profile?.wallet?.totalEarned;
    const reservedWithdrawalTotal = withdrawals
        .filter((w) => ["pending", "processing", "approved", "paid"].includes(String(w.status || "").toLowerCase()))
        .reduce((sum, w) => sum + Number(w.amount || 0), 0);
    const balance = Math.max(0, (savedBalance == null || Number(savedBalance) === 0) && deliveredOwnerEarnings > 0
        ? deliveredOwnerEarnings - reservedWithdrawalTotal
        : Number(savedBalance ?? 0) - withdrawals
            .filter((w) => ["pending", "processing", "approved"].includes(String(w.status || "").toLowerCase()))
            .reduce((sum, w) => sum + Number(w.amount || 0), 0));
    const earned = Number((savedEarned == null || Number(savedEarned) === 0) && deliveredOwnerEarnings > 0 ? deliveredOwnerEarnings : (savedEarned ?? 0));
    const ownerNotifications = [...orders]
        .sort((a, b) => {
        const ms = (v) => v?.toDate ? v.toDate().getTime() : new Date(v || 0).getTime();
        return ms(b.updatedAt || b.createdAt) - ms(a.updatedAt || a.createdAt);
    })
        .slice(0, 8)
        .map((o) => ({
        id: o.id,
        title: `Order ${String(o.status || o.orderStatus || "received").replaceAll("_", " ")}`,
        message: `Order ${o.orderNumber || o.id} · ${money(o.totalAmount ?? o.customerPaid ?? o.amount ?? 0)}`,
        date: o.updatedAt || o.createdAt,
    }));
    const unreadOwnerNotifications = ownerNotifications.filter((n) => !readNotificationIds.includes(n.id));
    const markAllNotificationsRead = () => {
        const next = Array.from(new Set([...readNotificationIds, ...ownerNotifications.map((n) => n.id)]));
        setReadNotificationIds(next);
        try {
            window.localStorage.setItem("sareeStudioShopOwnerReadNotifications", JSON.stringify(next));
        }
        catch (storageError) {
            console.warn("Could not persist notification read state", storageError);
        }
    };
    const filteredProducts = useMemo(() => {
        const t = search.trim().toLowerCase();
        return !t
            ? products
            : products.filter((p) => [p.name, p.title, p.category, p.sku].some((v) => String(v || "").toLowerCase().includes(t)));
    }, [products, search]);
    const filteredOrders = useMemo(() => orderFilter === "all"
        ? orders
        : orders.filter((o) => String(o.status || o.orderStatus || "pending").toLowerCase() ===
            orderFilter), [orders, orderFilter]);
    const openNew = () => {
        setEditing(null);
        setForm(initialForm);
        setImageFile(null);
        setError("");
        setNotice("");
        setModal(true);
    };
    const openEdit = (p) => {
        setEditing(p);
        setForm({
            name: p.name || p.title || "",
            description: p.description || "",
            category: p.category || "Silk",
            fixedPrice: String(p.fixedPrice ?? p.wholesalePrice ?? p.price ?? ""),
            stock: String(p.stock ?? p.stockQuantity ?? ""),
        });
        setImageFile(null);
        setError("");
        setNotice("");
        setModal(true);
    };
    const saveProduct = async (e) => {
        e.preventDefault();
        setError("");
        setNotice("");
        const price = Number(form.fixedPrice);
        const stock = Number(form.stock);
        if (!form.name.trim()) {
            return setError("Please enter a saree name.");
        }
        if (!Number.isFinite(price) || price <= 0) {
            return setError("Enter a valid fixed/wholesale price.");
        }
        if (!Number.isInteger(stock) || stock < 0) {
            return setError("Stock must be a whole number of 0 or more.");
        }
        if (!editing && !imageFile) {
            return setError("Please upload a product photo.");
        }
        setSaving(true);
        try {
            let imageUrl = editing?.imageUrl || editing?.image || "";
            if (imageFile) {
                imageUrl = await compressImageToDataUrl(imageFile);
            }
            const data = {
                name: form.name.trim(),
                title: form.name.trim(),
                description: form.description.trim(),
                category: form.category,
                fixedPrice: price,
                wholesalePrice: price,
                price,
                stock,
                stockQuantity: stock,
                imageUrl,
                image: imageUrl,
                imageStorage: "firestore-data-url",
                shopOwnerId: user.uid,
                shopOwnerName: profile?.shopName ||
                    profile?.name ||
                    user.displayName ||
                    "Shop Owner",
                status: editing?.status || "active",
                visibility: editing?.visibility || "active",
                updatedAt: serverTimestamp(),
            };
            if (editing) {
                await updateDoc(doc(db, "products", editing.id), data);
                setNotice("Saree updated successfully.");
            }
            else {
                await addDoc(collection(db, "products"), {
                    ...data,
                    createdAt: serverTimestamp(),
                });
                setNotice("Saree uploaded successfully.");
            }
            setModal(false);
        }
        catch (e2) {
            setError(`Could not save saree: ${e2.message}`);
        }
        finally {
            setSaving(false);
        }
    };
    const toggleProduct = async (p) => {
        setError("");
        setNotice("");
        const hidden = [
            "hidden",
            "inactive",
            "disabled",
            "archived",
        ].includes(String(p.status || p.visibility || "active").toLowerCase());
        try {
            await updateDoc(doc(db, "products", p.id), {
                status: hidden ? "active" : "hidden",
                visibility: hidden ? "active" : "hidden",
                updatedAt: serverTimestamp(),
            });
            setNotice(hidden ? "Saree is live again." : "Saree hidden from catalogue.");
        }
        catch (e) {
            setError(`Could not update product: ${e.message}`);
        }
    };
    const updateOrder = async (order, status) => {
        setError("");
        setNotice("");
        try {
            await updateDoc(doc(db, "orders", order.id), {
                status,
                orderStatus: status,
                updatedAt: serverTimestamp(),
            });
            setNotice(`Order marked ${pretty(status)}.`);
        }
        catch (e) {
            setError(`Could not update order: ${e.message}`);
        }
    };
    const requestWithdrawal = async (e) => {
        e.preventDefault();
        setError("");
        setNotice("");
        const amount = Number(withdrawAmount);
        if (!Number.isFinite(amount) || amount < 100) {
            return setError("Minimum withdrawal request is ₹100.");
        }
        if (amount > balance) {
            return setError("Amount exceeds the displayed available balance.");
        }
        if (!/^[\w.-]+@[\w.-]+$/.test(upi.trim())) {
            return setError("Enter a valid UPI ID, e.g. name@bank.");
        }
        try {
            await addDoc(collection(db, "withdrawals"), {
                shopOwnerId: user.uid,
                userId: user.uid,
                ownerName: profile?.name || user.displayName || "",
                shopName: profile?.shopName || "",
                amount,
                upiId: upi.trim(),
                status: "pending",
                createdAt: serverTimestamp(),
            });
            setWithdrawAmount("");
            setNotice("Withdrawal request submitted for Admin review. No money has been transferred.");
        }
        catch (e2) {
            setError(`Could not submit request: ${e2.message}`);
        }
    };
    // eslint-disable-next-line no-unused-vars
    const askSupport = async (e) => {
        e.preventDefault();
        setError("");
        setNotice("");
        if (!question.trim()) {
            return setError("Please enter your question.");
        }
        try {
            await addDoc(collection(db, "supportTickets"), {
                userId: user.uid,
                role: "shop-owner",
                name: profile?.name || user.displayName || "",
                shopName: profile?.shopName || "",
                message: question.trim(),
                status: "open",
                createdAt: serverTimestamp(),
            });
            setQuestion("");
            setNotice("Your support question has been submitted.");
        }
        catch (e2) {
            setError(`Could not submit question: ${e2.message}`);
        }
    };
    const logout = async () => {
        await signOut(auth);
        window.location.assign("/signin");
    };
    if (loading) {
        return (<div className="flex min-h-screen items-center justify-center bg-[#faf8f6] text-stone-600">
        <RefreshCw className="mr-2 animate-spin" size={18}/>
        Loading shop dashboard…
      </div>);
    }
    if (!user) {
        return (<div className="flex min-h-screen flex-col items-center justify-center bg-[#faf8f6] p-6 text-center">
        <h1 className="font-serif text-2xl text-rose-950">
          Please sign in
        </h1>
        <p className="mt-2 text-sm text-stone-600">
          Sign in with your approved Shop Owner account.
        </p>
        <a href="/signin" className="mt-4 rounded-xl bg-rose-900 px-5 py-3 text-sm font-semibold text-white">
          Go to sign in
        </a>
      </div>);
    }
    if (!profile) {
        return (<div className="min-h-screen bg-[#faf8f6] p-8">
        <h1 className="text-2xl font-semibold text-rose-950">
          Profile not found
        </h1>
        <p className="mt-2 text-stone-600">
          Could not read your users profile.
        </p>
      </div>);
    }
    if (profile.role !== "shop-owner" ||
        !["approved", "active"].includes(profile.status)) {
        return (<div className="flex min-h-screen items-center justify-center bg-[#faf8f6] p-6">
        <div className="max-w-lg rounded-3xl border border-stone-200 bg-white p-8 text-center shadow-sm">
          <Clock3 className="mx-auto text-amber-700" size={32}/>
          <h1 className="mt-4 font-serif text-2xl text-rose-950">
            Application awaiting approval
          </h1>
          <p className="mt-2 text-sm leading-6 text-stone-600">
            Hi {profile.name || user.displayName || "there"}, your Shop
            Owner application is {pretty(profile.status || "pending")}.
            Admin approval is required before the dashboard unlocks.
          </p>
          <button onClick={logout} className="mt-6 rounded-xl bg-rose-900 px-5 py-3 text-sm font-semibold text-white">
            Sign out
          </button>
        </div>
      </div>);
    }
    const title = nav.find((n) => n.id === tab)?.label || "Home";
    const homePage = () => (<>
      <div className="mb-7 flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-amber-700">
            Shop owner workspace
          </p>
          <h1 className="mt-2 font-serif text-3xl text-rose-950">
            Welcome, {profile.name || user.displayName || "Shop Owner"}
          </h1>
          <p className="mt-2 text-sm text-stone-600">
            Here’s what’s happening with {profile.shopName || "your shop"} today.
          </p>
        </div>
        <button onClick={openNew} className="rounded-xl bg-rose-900 px-4 py-3 text-sm font-semibold text-white">
          <Plus className="mr-2 inline" size={17}/>
          Upload saree
        </button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        <Stat icon={Shirt} label="Live sarees" value={live.length} note="Visible in your catalogue"/>
        <Stat icon={PackageCheck} label="Orders to pack" value={toPack.length} note="Awaiting packing" tone="gold"/>
        <Stat icon={Truck} label="Ready / in transit" value={ready.length + transit.length} note={`${ready.length} ready · ${transit.length} in transit`} tone="blue"/>
        <Stat icon={Wallet} label="Available wallet" value={money(balance)} note="Saved profile balance" tone="green"/>
        <Stat icon={IndianRupee} label="Total earned" value={money(earned)} note="Saved profile total"/>
        <Stat icon={CircleAlert} label="Low stock" value={lowStock.length} note="3 or fewer units" tone="gold"/>
      </div>

      <div className="mt-8 rounded-2xl border border-stone-200 bg-white p-5">
        <h2 className="font-serif text-xl text-rose-950">Recent orders</h2>
        {!orders.length ? (<p className="mt-4 text-sm text-stone-500">
            No orders yet. Orders appear when checkout saves the shopOwnerId field.
          </p>) : (orders.slice(0, 5).map((o) => (<div key={o.id} className="flex flex-wrap justify-between gap-3 border-b py-4">
              <div>
                <p className="font-semibold">#{o.orderNumber || o.id.slice(0, 8)}</p>
                <p className="text-sm text-stone-500">
                  {o.customerName || o.customer?.name || "Customer"} · {dateText(o.createdAt)}
                </p>
              </div>
              <div>
                <p className="font-semibold">{money(o.totalAmount ?? o.total ?? o.amount)}</p>
                <Pill status={o.status || o.orderStatus}/>
              </div>
            </div>)))}

        <button onClick={() => setTab("orders")} className="mt-4 text-sm font-semibold text-rose-800">
          View all orders →
        </button>
      </div>

      <div className="mt-5 rounded-2xl border border-stone-200 bg-white p-5">
        <h2 className="font-serif text-xl text-rose-950">Shop information</h2>
        <p className="mt-2 text-sm">{profile.shopName || "Shop name not set"}</p>
        <p className="mt-2 break-all text-xs text-stone-500">
          Firebase UID: {user.uid}
        </p>
        <p className="mt-3 text-sm text-stone-500">
          Low-stock sarees: {lowStock.length}
        </p>
      </div>
    </>);
    const productsPage = () => (<>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl text-rose-950">
            Upload & Edit Sarees
          </h1>
          <p className="mt-2 text-sm text-stone-600">
            Fixed price shop-owner price hai. Reseller apna margin alag add karega.
          </p>
        </div>
        <button onClick={openNew} className="rounded-xl bg-rose-900 px-4 py-3 text-white">
          <Plus className="mr-2 inline" size={17}/>
          Upload saree
        </button>
      </div>

      <div className="mb-4 flex flex-wrap gap-3">
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search sarees..." className="min-w-0 flex-1 rounded-xl border border-stone-200 bg-white px-4 py-3"/>
        <span className="rounded-xl border bg-white px-4 py-3 text-sm">
          {products.length} products · {live.length} live
        </span>
      </div>

      {!filteredProducts.length ? (<Empty title="No sarees uploaded yet" text="Upload your first saree with a photo, price and stock."/>) : (<div className="overflow-x-auto rounded-2xl border bg-white">
          <table className="w-full min-w-[650px] text-left text-sm">
            <thead className="bg-stone-50">
              <tr>
                <th className="p-4">Saree</th>
                <th className="p-4">Fixed price</th>
                <th className="p-4">Stock</th>
                <th className="p-4">Status</th>
                <th className="p-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.map((p) => {
                const hidden = [
                    "hidden",
                    "inactive",
                    "disabled",
                    "archived",
                ].includes(String(p.status || p.visibility || "active").toLowerCase());
                return (<tr key={p.id} className="border-t">
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        {(p.imageUrl || p.image || p.photoURL) && (<img src={p.imageUrl || p.image || p.photoURL} alt={p.name || p.title || "Saree"} className="h-14 w-12 rounded-lg object-cover"/>)}
                        <div>
                          <p className="font-semibold">{p.name || p.title}</p>
                          <p className="text-xs text-stone-500">
                            {p.category || "Saree"}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      {money(p.fixedPrice ?? p.wholesalePrice ?? p.price)}
                    </td>
                    <td className="p-4">{p.stock ?? p.stockQuantity ?? 0}</td>
                    <td className="p-4">
                      <Pill status={hidden ? "hidden" : "live"}/>
                    </td>
                    <td className="p-4">
                      <div className="flex gap-2">
                        <button onClick={() => openEdit(p)} title="Edit" className="rounded-lg border p-2">
                          <Pencil size={16}/>
                        </button>
                        <button onClick={() => toggleProduct(p)} title="Hide or show" className="rounded-lg border p-2">
                          {hidden ? <Eye size={16}/> : <EyeOff size={16}/>}
                        </button>
                      </div>
                    </td>
                  </tr>);
            })}
            </tbody>
          </table>
        </div>)}
    </>);
    const ordersPage = () => (<>
      <h1 className="font-serif text-3xl text-rose-950">Orders to Pack</h1>
      <p className="mb-5 mt-2 text-sm text-stone-600">
        Customer orders linked to your shop appear here.
      </p>

      <div className="mb-5 flex flex-wrap gap-2">
        {[
            ["all", "All orders"],
            ["pending", "To pack"],
            ["ready_to_ship", "Ready to ship"],
            ["in_transit", "In transit"],
            ["delivered", "Delivered"],
        ].map(([value, label]) => (<button key={value} onClick={() => setOrderFilter(value)} className={`rounded-full px-4 py-2 text-sm ${orderFilter === value
                ? "bg-rose-900 text-white"
                : "border bg-white"}`}>
            {label}
          </button>))}
      </div>

      {!filteredOrders.length ? (<Empty title="No matching orders" text="Orders will appear when checkout is connected."/>) : (<div className="overflow-x-auto rounded-2xl border bg-white">
          <table className="w-full min-w-[700px] text-left text-sm">
            <thead className="bg-stone-50">
              <tr>
                <th className="p-4">Order</th>
                <th className="p-4">Customer</th>
                <th className="p-4">Amount</th>
                <th className="p-4">Status</th>
                <th className="p-4">Update</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map((o) => {
                const status = String(o.status || o.orderStatus || "pending").toLowerCase();
                return (<tr key={o.id} className="border-t">
                    <td className="p-4">
                      #{o.orderNumber || o.id.slice(0, 8)}
                    </td>
                    <td className="p-4">
                      <p className="font-semibold">{o.customerName || o.customer?.name || "Customer"}</p>
                      {o.customerPhone && <p className="mt-1 text-xs text-stone-500">Phone: {o.customerPhone}</p>}
                      {(o.shippingAddress || o.shippingCity || o.shippingPincode) && <p className="mt-1 max-w-xs whitespace-normal text-xs text-stone-500">{[o.shippingAddress, o.shippingCity, o.shippingPincode].filter(Boolean).join(", ")}</p>}
                      {Array.isArray(o.items) && <div className="mt-2 space-y-1">{o.items.map((item, index) => <p key={`${item.productId || item.productName}-${index}`} className="text-xs text-stone-600">{item.productName || "Saree"} × {item.quantity || 1} · {money((Number(item.shopOwnerPrice) || 0) * (Number(item.quantity) || 1))}</p>)}</div>}
                      {(o.paymentScreenshotData || o.paymentScreenshotURL || o.paymentScreenshotUrl) && <div className="mt-3 flex flex-wrap items-start gap-3"><a href={o.paymentScreenshotData || o.paymentScreenshotURL || o.paymentScreenshotUrl} target="_blank" rel="noreferrer" className="block"><img src={o.paymentScreenshotData || o.paymentScreenshotURL || o.paymentScreenshotUrl} alt="Customer payment screenshot" loading="lazy" className="h-24 w-24 rounded-lg border border-stone-200 bg-stone-50 object-contain"/><span className="mt-1 block text-xs font-semibold text-rose-800 underline">Preview screenshot</span></a><a href={o.paymentScreenshotData || o.paymentScreenshotURL || o.paymentScreenshotUrl} download={`payment-proof-${o.id}.jpg`} target="_blank" rel="noreferrer" className="rounded-lg border border-rose-200 px-3 py-2 text-xs font-semibold text-rose-900 hover:bg-rose-50">Download screenshot</a></div>}
                    </td>
                    <td className="p-4">
                      <p className="font-semibold">{money(o.shopOwnerSubtotal ?? o.totalAmount ?? o.total ?? o.amount)}</p>
                      {Number(o.resellerEarning) > 0 && <p className="mt-1 text-xs text-stone-500">Reseller margin: {money(o.resellerEarning)}</p>}
                    </td>
                    <td className="p-4"><Pill status={status}/></td>
                    <td className="p-4">
                      <select value={status} onChange={(e) => updateOrder(o, e.target.value)} className="rounded-lg border bg-white p-2">
                        <option value="pending">To pack</option>
                        <option value="processing">Processing</option>
                        <option value="ready_to_ship">Ready to ship</option>
                        <option value="in_transit">In transit</option>
                        <option value="delivered">Delivered</option>
                      </select>
                    </td>
                  </tr>);
            })}
            </tbody>
          </table>
        </div>)}
    </>);
    const walletPage = () => (<>
      <h1 className="font-serif text-3xl text-rose-950">Wallet & Withdraw</h1>

      <div className="mt-5 grid gap-4 md:grid-cols-2">
        <div className="rounded-2xl bg-rose-950 p-6 text-white">
          <p>Available balance</p>
          <p className="mt-3 text-3xl font-bold">{money(balance)}</p>
        </div>
        <div className="rounded-2xl border bg-white p-4 sm:p-6">
          <p className="text-stone-500">Total earned</p>
          <p className="mt-3 text-3xl font-bold">{money(earned)}</p>
        </div>
      </div>

      <form onSubmit={requestWithdrawal} className="mt-5 max-w-xl rounded-2xl border bg-white p-4 sm:p-6">
        <h2 className="font-serif text-xl text-rose-950">
          Request a withdrawal
        </h2>

        <label className="mt-4 block text-sm font-medium">
          Amount (₹)
          <input type="number" min="1" step="1" required value={withdrawAmount} onChange={(e) => setWithdrawAmount(e.target.value)} className="mt-2 w-full rounded-xl border p-3"/>
        </label>

        <label className="mt-4 block text-sm font-medium">
          UPI ID
          <input required value={upi} onChange={(e) => setUpi(e.target.value)} placeholder="name@bank" className="mt-2 w-full rounded-xl border p-3"/>
        </label>

        <button className="mt-5 w-full rounded-xl bg-rose-900 p-3 font-semibold text-white">
          Submit withdrawal request
        </button>
        <p className="mt-3 text-xs text-stone-500">
          Admin approval and an actual payout process are required.
        </p>
      </form>

      <section className="mt-5 rounded-2xl border bg-white p-4 sm:p-6">
        <h2 className="font-serif text-xl text-rose-950">
          Withdrawal requests
        </h2>
        {!withdrawals.length ? (<p className="mt-3 text-sm text-stone-500">No requests found.</p>) : (withdrawals.map((w) => (<div key={w.id} className="flex justify-between gap-3 border-b py-3">
              <div>
                <p className="font-semibold">{money(w.amount)}</p>
                <p className="text-xs text-stone-500">
                  {dateText(w.createdAt)} · {w.upiId}
                </p>
              </div>
              <Pill status={w.status}/>
            </div>)))}
      </section>

      <section className="mt-5 rounded-2xl border bg-white p-4 sm:p-6">
        <h2 className="font-serif text-xl text-rose-950">Wallet history</h2>
        {!transactions.length ? (<p className="mt-3 text-sm text-stone-500">
            No transactions recorded yet.
          </p>) : (transactions.map((t) => (<div key={t.id} className="flex justify-between gap-3 border-b py-3">
              <div>
                <p>{t.description || t.title || t.type || "Wallet transaction"}</p>
                <p className="text-xs text-stone-500">
                  {dateText(t.createdAt || t.date)}
                </p>
              </div>
              <p className="font-semibold">{money(t.amount)}</p>
            </div>)))}
      </section>
    </>);
    const trainingPage = () => (<>
      <div className="mb-6"><h2 className="font-serif text-2xl text-rose-950 sm:text-3xl">Enquire / Support Questions</h2><p className="mt-2 text-sm leading-6 text-stone-500">Submit an issue and check the admin reply below. Replies appear here automatically.</p></div>
      <form onSubmit={async (event) => {
        event.preventDefault(); setEnquiryNotice("");
        if (!user?.uid || !enquiryMessage.trim()) { setEnquiryNotice("Please enter your issue before submitting."); return; }
        setEnquirySaving(true);
        try {
          await addDoc(collection(db, "enquiries"), { userId: user.uid, uid: user.uid, shopOwnerId: user.uid, accountType: "shopOwner", name: profile?.fullName || profile?.name || profile?.displayName || "Shop Owner", email: profile?.email || user.email || "", subject: enquirySubject, message: enquiryMessage.trim(), status: "new", adminReply: "", createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
          setEnquiryMessage(""); setEnquiryNotice("Enquiry submitted successfully. Admin will reply from the Enquiries section.");
        } catch (error) { console.error("Enquiry submit failed", error); setEnquiryNotice("Could not submit enquiry. Please check your connection or Firestore rules and try again."); }
        finally { setEnquirySaving(false); }
      }} className="max-w-3xl rounded-2xl border border-stone-200 bg-white p-5 sm:p-7">
        <label className="block text-sm font-semibold text-stone-700">What do you need help with?
          <select value={enquirySubject} onChange={(e) => setEnquirySubject(e.target.value)} className="mt-2 w-full rounded-xl border border-stone-300 px-4 py-3 font-normal"><option>Order / delivery issue</option><option>Wallet / withdrawal</option><option>Product / stock issue</option><option>Payment issue</option><option>Account / login issue</option><option>Other</option></select>
        </label>
        <label className="mt-4 block text-sm font-semibold text-stone-700">Describe your issue
          <textarea required maxLength={3000} rows={5} value={enquiryMessage} onChange={(e) => setEnquiryMessage(e.target.value)} placeholder="Explain the issue and include an order ID if relevant..." className="mt-2 w-full rounded-xl border border-stone-300 px-4 py-3 font-normal" />
        </label>
        {enquiryNotice && <p role="status" className="mt-4 rounded-xl bg-rose-50 p-3 text-sm text-rose-900">{enquiryNotice}</p>}
        <button disabled={enquirySaving} className="mt-5 rounded-xl bg-rose-900 px-5 py-3 text-sm font-bold text-white disabled:opacity-50">{enquirySaving ? "Submitting..." : "Submit enquiry"}</button>
      </form>
      <section className="mt-6 max-w-3xl rounded-2xl border border-stone-200 bg-white p-5 sm:p-7">
        <h3 className="font-serif text-xl text-rose-950">My enquiries and replies</h3>
        {!enquiries.length ? <p className="mt-3 text-sm text-stone-500">You have not submitted any enquiries yet.</p> : <div className="mt-4 space-y-3">{[...enquiries].sort((a,b) => { const time = (v) => v?.toDate ? v.toDate().getTime() : new Date(v || 0).getTime(); return time(b.createdAt)-time(a.createdAt); }).map((item) => <article key={item.id} className="rounded-xl border border-stone-200 p-4"><div className="flex flex-wrap items-center justify-between gap-2"><p className="font-semibold text-stone-900">{item.subject || "Support enquiry"}</p><span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${item.adminReply ? "bg-emerald-50 text-emerald-800" : "bg-amber-50 text-amber-800"}`}>{item.adminReply ? "Admin replied" : String(item.status || "new").replaceAll("_", " ")}</span></div><p className="mt-2 whitespace-pre-wrap text-sm text-stone-600">{item.message}</p>{item.adminReply ? <div className="mt-3 rounded-lg bg-emerald-50 p-3"><p className="text-xs font-bold text-emerald-800">Admin reply</p><p className="mt-1 whitespace-pre-wrap text-sm text-emerald-950">{item.adminReply}</p></div> : <p className="mt-3 text-xs text-stone-500">Waiting for the admin to reply.</p>}</article>)}</div>}
      </section>
    </>);

    const content = {
        home: homePage,
        products: productsPage,
        orders: ordersPage,
        wallet: walletPage,
        training: trainingPage,
    };
    return (<div className="min-h-screen overflow-x-hidden bg-[#faf8f6] text-stone-800">
      {mobileOpen && (<button aria-label="Close navigation" onClick={() => setMobileOpen(false)} className="fixed inset-0 z-40 bg-black/40 lg:hidden"/>)}

      <aside className={`fixed inset-y-0 left-0 z-50 flex w-[270px] flex-col border-r border-stone-200 bg-white transition-transform lg:translate-x-0 ${mobileOpen ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="flex items-center justify-between border-b px-5 py-5">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-rose-950 text-amber-200">
              <Shirt size={23}/>
            </div>
            <div>
              <p className="font-serif text-lg font-bold text-rose-950">
                Vasthra
              </p>
              <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-stone-500">
                Shop owner panel
              </p>
            </div>
          </div>
          <button onClick={() => setMobileOpen(false)} className="rounded-lg p-2 lg:hidden">
            <X size={19}/>
          </button>
        </div>

        <div className="border-b px-5 py-4">
          <p className="text-xs text-stone-500">Signed in as</p>
          <p className="mt-1 truncate text-sm font-semibold">
            {profile.shopName || profile.name || user.email}
          </p>
          <p className="mt-1 truncate text-xs text-stone-500">
            {profile.email || user.email}
          </p>
        </div>

        <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4">
          <p className="px-3 pb-2 text-[10px] font-bold uppercase tracking-[0.18em] text-stone-400">
            Workspace
          </p>
          {nav.map(({ id, label, icon: Icon }) => (<button key={id} onClick={() => {
                setTab(id);
                setMobileOpen(false);
                setError("");
                setNotice("");
            }} className={`flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-medium transition ${tab === id
                ? "bg-rose-950 text-white"
                : "text-stone-600 hover:bg-stone-100"}`}>
              <Icon size={18}/>
              <span className="flex-1">{label}</span>
              {id === "orders" && toPack.length > 0 && (<span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-800">
                  {toPack.length}
                </span>)}
            </button>))}
        </nav>

        <div className="border-t p-4">
          <div className="mb-3 rounded-xl bg-amber-50 p-3">
            <p className="text-xs font-semibold text-amber-900">
              Keep your catalogue updated
            </p>
            <p className="mt-1 text-xs leading-5 text-amber-800">
              Accurate prices and stock help avoid order issues.
            </p>
          </div>
          <button onClick={logout} className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-sm font-semibold text-stone-600 hover:bg-red-50 hover:text-red-700">
            <LogOut size={18}/> Sign out
          </button>
        </div>
      </aside>

      <div className="lg:pl-[270px]">
        <header className="sticky top-0 z-30 flex min-h-[64px] items-center justify-between gap-2 border-b border-stone-200 bg-[#faf8f6]/95 px-3 py-2 backdrop-blur sm:min-h-[72px] sm:px-7">
          <div className="flex min-w-0 items-center gap-3">
            <button onClick={() => setMobileOpen(true)} className="rounded-xl border bg-white p-2 lg:hidden">
              <Menu size={19}/>
            </button>
            <div>
              <p className="truncate text-xs text-stone-500">
                Shop Owner / {title}
              </p>
              <p className="truncate font-semibold">{title}</p>
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <div className="hidden text-right sm:block">
              <p className="text-sm font-semibold">
                {profile.name || user.displayName || "Shop Owner"}
              </p>
              <p className="text-xs text-stone-500">
                {profile.shopName || "My shop"}
              </p>
            </div>
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-rose-950 font-semibold text-white">
              {String(profile.name || user.displayName || "S")
            .trim()
            .charAt(0)
            .toUpperCase()}
            </div>
            <div className="relative">
              <button type="button" title="Notifications" aria-label="Notifications" aria-expanded={notificationsOpen} onClick={() => setNotificationsOpen((open) => !open)} className="relative rounded-xl border bg-white p-2.5 text-stone-600">
                <Bell size={18}/>
                {unreadOwnerNotifications.length > 0 && <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-rose-800 px-1 text-[10px] font-bold text-white">{unreadOwnerNotifications.length}</span>}
              </button>
              {notificationsOpen && <div className="absolute right-0 top-12 z-50 w-[min(90vw,360px)] overflow-hidden rounded-2xl border bg-white shadow-xl">
                <div className="flex items-center justify-between gap-3 border-b px-4 py-3"><p className="font-semibold">Recent order notifications</p><div className="flex items-center gap-3"><button type="button" onClick={markAllNotificationsRead} className="text-xs font-semibold text-rose-800 underline">Mark all read</button><button type="button" onClick={() => setNotificationsOpen(false)} aria-label="Close notifications">×</button></div></div>
                <div className="max-h-96 overflow-y-auto">
                  {!ownerNotifications.length ? <p className="p-5 text-sm text-stone-500">No orders yet. New orders will appear here.</p> : ownerNotifications.map((n) => <button key={n.id} type="button" onClick={() => { markAllNotificationsRead(); setTab("orders"); setNotificationsOpen(false); }} className={`block w-full border-b px-4 py-3 text-left hover:bg-rose-50 ${readNotificationIds.includes(n.id) ? "bg-white" : "bg-rose-50/70"}`}><span className="block text-sm font-semibold capitalize">{n.title} {!readNotificationIds.includes(n.id) && <span className="ml-1 inline-block h-2 w-2 rounded-full bg-rose-700"/>}</span><span className="mt-1 block text-sm text-stone-600">{n.message}</span><span className="mt-1 block text-xs text-stone-400">{dateText(n.date)}</span></button>)}
                </div>
              </div>}
            </div>
          </div>
        </header>

        <main className="mx-auto min-w-0 max-w-[1500px] p-3 sm:p-5 lg:p-7">
          {error && (<div role="alert" className="mb-4 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">
              <CircleAlert size={17} className="mt-0.5 shrink-0"/>
              <span>{error}</span>
              <button onClick={() => setError("")} className="ml-auto font-bold">
                ×
              </button>
            </div>)}

          {notice && (<div role="status" className="mb-4 flex items-start gap-2 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
              <CheckCircle2 size={17} className="mt-0.5 shrink-0"/>
              <span>{notice}</span>
              <button onClick={() => setNotice("")} className="ml-auto font-bold">
                ×
              </button>
            </div>)}

          {content[tab]?.()}

          <footer className="mt-10 border-t border-stone-200 py-5 text-xs text-stone-400">
            © {new Date().getFullYear()} Vasthra · Shop Owner Workspace
          </footer>
        </main>
      </div>

      {modal && (<div className="fixed inset-0 z-[70] flex items-center justify-center overflow-y-auto bg-black/50 p-4">
          <div role="dialog" aria-modal="true" className="my-6 w-full max-w-2xl rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b px-5 py-4 sm:px-6">
              <div>
                <h2 className="font-serif text-2xl text-rose-950">
                  {editing ? "Edit saree" : "Upload a saree"}
                </h2>
                <p className="mt-1 text-sm text-stone-500">
                  Product details and fixed price
                </p>
              </div>
              <button onClick={() => setModal(false)} className="rounded-lg p-2 text-stone-500">
                <X size={19}/>
              </button>
            </div>

            <form onSubmit={saveProduct} className="space-y-4 p-5 sm:p-6">
              <label className="block text-sm font-medium">
                Saree name *
                <input value={form.name} onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))} className="mt-2 w-full rounded-xl border px-4 py-3" placeholder="e.g. Banarasi Silk Saree" required/>
              </label>

              <label className="block text-sm font-medium">
                Description
                <textarea value={form.description} onChange={(e) => setForm((s) => ({ ...s, description: e.target.value }))} rows={3} className="mt-2 w-full rounded-xl border px-4 py-3" placeholder="Fabric, colour, occasion, care details..."/>
              </label>

              <div className="grid gap-4 sm:grid-cols-3">
                <label className="block text-sm font-medium">
                  Category
                  <select value={form.category} onChange={(e) => setForm((s) => ({ ...s, category: e.target.value }))} className="mt-2 w-full rounded-xl border bg-white px-3 py-3">
                    <option>Silk</option>
                    <option>Banarasi</option>
                    <option>Cotton</option>
                    <option>Kanjivaram</option>
                    <option>Georgette</option>
                    <option>Chiffon</option>
                    <option>Linen</option>
                    <option>Other</option>
                  </select>
                </label>

                <label className="block text-sm font-medium">
                  Fixed price (₹) *
                  <input type="number" min="1" step="1" value={form.fixedPrice} onChange={(e) => setForm((s) => ({ ...s, fixedPrice: e.target.value }))} className="mt-2 w-full rounded-xl border px-3 py-3" placeholder="1150" required/>
                </label>

                <label className="block text-sm font-medium">
                  Stock quantity *
                  <input type="number" min="0" step="1" value={form.stock} onChange={(e) => setForm((s) => ({ ...s, stock: e.target.value }))} className="mt-2 w-full rounded-xl border px-3 py-3" placeholder="10" required/>
                </label>
              </div>

              <label className="block text-sm font-medium">
                Product photo {!editing && "*"}
                <div className="mt-2 flex cursor-pointer items-center gap-3 rounded-xl border border-dashed border-stone-300 p-4 hover:border-rose-400">
                  <ImagePlus className="text-rose-800" size={24}/>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold">
                      {imageFile?.name ||
                (editing
                    ? "Choose a new photo (optional)"
                    : "Choose saree photo")}
                    </p>
                    <p className="mt-1 text-xs text-stone-500">
                      JPG, PNG or WebP · max 10 MB before compression
                    </p>
                  </div>
                  <Upload size={17}/>
                  <input type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => {
                const file = e.target.files?.[0] || null;
                setError("");
                setImageFile(null);
                if (!file)
                    return;
                if (!file.type?.startsWith("image/")) {
                    setError("Please choose an image file.");
                    e.target.value = "";
                    return;
                }
                if (file.size > 10 * 1024 * 1024) {
                    setError("Choose an image smaller than 10 MB. It will be compressed before saving.");
                    e.target.value = "";
                    return;
                }
                setImageFile(file);
            }} required={!editing} className="sr-only"/>
                </div>
              </label>

              <div className="rounded-xl bg-amber-50 p-3 text-xs leading-5 text-amber-900">
                Fixed price means the shop-owner price. Resellers add their own
                margin separately. Photos are compressed and stored with the product
                document (not Firebase Storage); use small photos because Firestore
                documents have a strict 1 MiB limit.
              </div>

              <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:justify-end">
                <button type="button" onClick={() => setModal(false)} className="rounded-xl border px-5 py-3 text-sm font-semibold">
                  Cancel
                </button>
                <button disabled={saving} className="rounded-xl bg-rose-900 px-5 py-3 text-sm font-semibold text-white disabled:opacity-60">
                  {saving
                ? "Saving..."
                : editing
                    ? "Save changes"
                    : "Upload saree"}
                </button>
              </div>
            </form>
          </div>
        </div>)}
    </div>);
}
